import { useState, useEffect, useContext, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import {
  Badge,
  Card,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
} from "@mui/material";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import { Link } from "react-router-dom";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";
import ContactAvatar from "components/UpStyled/ContactAvatar";
import NumberFormat from "react-number-format";
import curved9 from "assets/images/upstyled/default_background.png";
import { AccountContext } from "context/account";
import moment from "moment";
import { canResizeImageUrl, getStyleImages, getFileImage } from "utils/imageHelper";
import ProjectStatusTag from "components/UpStyled/ProjectStatusTag";
import Prompt from "components/UpStyled/Prompt";
import SuiAvatar from "components/SuiAvatar";
import DeleteIcon from "@mui/icons-material/Delete";
import ClipLoader from "react-spinners/ClipLoader";
import api from "services/api";
import toast from "react-hot-toast";
import { backendServer } from "services/request";
import socketIOClient from "socket.io-client";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";

export function RemoveProspectDialog({
  cancelText = "Cancel",
  okText = "Ok",
  okColor,
  cancelColor,
  visible,
  handleClose,
  handleOk,
  loading = false,
  option = {},
  project,
  userid,
}) {
  //console.log('project', project)
  var mayNotify = project?.audiences?.includes(userid);
  const [sendNotify, setSendNotify] = useState(true);
  const [notifyMsg, setNotifyMsg] = useState(
    "Thank you for your interest! Unfortunately, we cannot assist you at this moment. We look forward to future opportunities to work with you! "
  );

  const socketRef = useRef(null);
  useEffect(() => {
    return () => {
      if (socketRef.current) socketRef.current?.disconnect();
    };
  }, []);

  const handleSendNotifyFlag = () => setSendNotify(!sendNotify);

  const handleClickOk = async () => {
    if (mayNotify && sendNotify && notifyMsg != "") {
      try {
        const { data } = await api.chatroom.addChatRoom({
          userids: [project.owner_id],
          regardingid: project.id,
        });
        var roomId = data.id;
        api.chatroom.sendmessage(roomId, { text: notifyMsg, type: 0 }).then(({ data }) => {
          socketRef.current = socketIOClient(backendServer, {
            query: { roomId },
          });
          socketRef.current.emit("newChatMessage", {
            body: notifyMsg,
            senderId: "NA",
            contacts: [project.owner_id],
          });
          return data;
        });
      } catch (err) {
        toast.error(`Send notification message for ${project.name} failed!`);
        console.log(err);
      }
    }
    handleOk();
  };

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      {...option}
    >
      <DialogTitle id="alert-dialog-title">{`Remove Interest`}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">{`Are you sure you want to remove ${project.name} from your list?`}</DialogContentText>
        {mayNotify && (
          <SuiBox>
            <SuiBox mt={1} mb={1} display="flex" alignItems="center">
              <Checkbox checked={sendNotify} onChange={handleSendNotifyFlag} />
              <SuiTypography
                variant="button"
                fontWeight="regular"
                onClick={handleSendNotifyFlag}
                sx={{ cursor: "pointer", userSelect: "none" }}
              >
                &nbsp;Send Notification Message To Designer
              </SuiTypography>
            </SuiBox>
            <SuiInput
              disabled={!sendNotify}
              multiline
              minRows={5}
              maxRows={5}
              value={notifyMsg}
              onChange={(event) => setNotifyMsg(event.target.value)}
            />
          </SuiBox>
        )}
      </DialogContent>
      <DialogActions>
        <SuiButton
          onClick={handleClose}
          color={cancelColor ? cancelColor : "secondary"}
          variant="gradient"
        >
          {cancelText}
        </SuiButton>
        <SuiButton
          onClick={handleClickOk}
          autoFocus
          color={okColor ? okColor : "dark"}
          variant="gradient"
        >
          {loading ? (
            <ClipLoader
              size={18}
              color={okColor ? okColor : "#000000"}
              cssOverride={{ marginRight: 1 }}
            />
          ) : null}
          <div>{okText}</div>
        </SuiButton>
      </DialogActions>
    </Dialog>
  );
}

function ProjectCard2({
  project = { services: [] },
  product = null,
  className,
  detailUrl = "#",
  actions,
  chats = null,
  removeProspect = null,
  handleNotInterested = null,
}) {
  const [images, setImages] = useState([]);
  const [removeProspectVisible, setRemoveProspectVisible] = useState(false);
  const metadata = project?.metadata ? project.metadata : {}; //project metadata
  const products = project?.products || [];

  const account = useContext(AccountContext);
  let userid = account.id;

  useEffect(() => {
    if (product) {
      let imageFiles = product?.style?.imageFiles || [];
      let techPacksFiles = product?.style?.techPacksFiles || [];
      let temp = [...imageFiles, ...techPacksFiles];

      if (account.account_type == "de") {
        temp = getStyleImages(product, true);
      }
      setImages(temp);
    } else {
      let styleImages = [];
      //add style first image
      for (let i = 0; i < products.length; i++) {
        let imageFiles = products[i]?.style?.imageFiles || [];
        let techPacksFiles = products[i]?.style?.techPacksFiles || [];

        let temp = [...imageFiles, ...techPacksFiles];
        if (account.account_type == "de") {
          temp = getStyleImages(products[i], false);
        }

        styleImages.push(...temp);
      }
      //add all project reference image
      const fileList = metadata["othersFiles"] || [];
      const files = fileList.filter((f) => f?.type && f.type.includes("image"));
      if (files && files.length > 0) {
        styleImages.push(...files);
      }
      if (styleImages.length == 0) {
        styleImages = [{ url: curved9, name: "deafult_image" }];
      }
      setImages(styleImages);
    }
  }, [products, metadata, product]);

  const servicesDetail = project?.quote?.services_required || {};
  const serviceRequired = Object.keys(servicesDetail);

  const formatProjectDate = (project) => {
    let prefix = "";
    let date = "";
    if (project.status == "active") {
      prefix = "Project was desired end at: ";
      date = project?.quote?.desired_end_date
        ? moment(project.quote.desired_end_date).format("yyyy-MM-DD")
        : "No Desired Date";
    } else if (project.status == "requested" || project.status == "init") {
      prefix = "Project was desired start at: ";
      date = project?.metadata?.desired_start_date
        ? moment(project.metadata.desired_start_date).format("yyyy-MM-DD")
        : "No Desired Date";
    } else if (project.status == "completed") {
      prefix = "Project finished at: ";
      date = project?.metadata?.completed_at
        ? moment(project.metadata.completed_at).format("yyyy-MM-DD")
        : "No Date";
    } else if (project.status == "canceled") {
      prefix = "Project canceled at: ";
      date = project?.metadata?.canceled_at
        ? moment(project.metadata.canceled_at).format("yyyy-MM-DD")
        : "No Date";
    }
    if (prefix) {
      return (
        <SuiTypography
          variant="body2"
          textTransform="capitalize"
          color="secondary"
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical",
          }}
        >
          {prefix} {date}
        </SuiTypography>
      );
    } else {
      return (
        <SuiTypography
          variant="body2"
          textTransform="capitalize"
          color="secondary"
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical",
          }}
        >
          N/A
        </SuiTypography>
      );
    }
  };

  const formatProjectExpectDate = (project) => {
    const endDate = project?.quote?.desired_end_date
      ? moment(project.quote.desired_end_date).format("MM/DD/yyyy")
      : "N/A";

    const startDate = project?.metadata?.desired_start_date
      ? moment(project.metadata.desired_start_date).format("MM/DD/yyyy")
      : "N/A";

    return (
      <SuiTypography variant="body2" textTransform="capitalize" color="secondary">
        {`Project was desired start at: ${startDate}, end at: ${endDate}`}
      </SuiTypography>
    );
  };

  const budgetMin = project?.quote?.budget_min;
  const budgetMax = project?.quote?.budget_max;
  var room = chats && chats.has(project.id) ? chats.get(project.id) : null;
  const smartdate = require("smartdate");
  smartdate.init();

  //get project owner name
  const ownerStore =
    project?.owner?.stores && project.owner.stores.length > 0 ? project.owner.stores[0] : {};
  const ownerName = project?.store_name ? project?.store_name : ownerStore?.name;
  const DesignerCom =
    account.account_type == "ma" ? (
      <SuiBox display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
        <SuiBox>
          <Tooltip position="top" title={ownerName}>
            <SuiAvatar
              src={ownerStore?.metadata?.avatar}
              alt="profile-image"
              variant="rounded"
              size="sm"
              shadow="sm"
            />
          </Tooltip>
        </SuiBox>
        <SuiBox>
          <SuiTypography variant="button" fontWeight="regular" textTransform="capitalize">
            {ownerName}
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    ) : null;

  return (
    <Card
      sx={{
        borderColor: ({ borders }) => borders.borderColor,
        boxShadow: ({ boxShadows: { md } }) => md,
        mb: 3,
      }}
    >
      <SuiBox display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
        <SuiBox
          position="relative"
          width="100.25%"
          sx={{ maxWidth: account.account_type == "ma" ? 200 : 280 }}
        >
          <Link to={detailUrl}>
            <ImageCardWrapper
              data={images && images.length > 0 ? images[0] : ""}
              height={account.account_type == "ma" ? 160 : 240}
              width={account.account_type == "ma" ? 200 : 280}
              sx={{ overflow: "hidden" }}
            />
          </Link>
        </SuiBox>
        <SuiBox position="relative" width="100%" sx={{ minWidth: 280 }}>
          <SuiBox py={1} pl={1} pr={4} sx={{ width: "100%" }}>
            <SuiBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
              <SuiBox>
                <Tooltip title={project.name}>
                  <SuiBox>
                    <SuiTypography
                      variant="h6"
                      fontWeight="medium"
                      textTransform="capitalize"
                      lineHeight="1.2"
                      sx={{
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {project.name}
                    </SuiTypography>
                  </SuiBox>
                </Tooltip>
              </SuiBox>

              <SuiBox>
                <SuiBox display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                  <SuiBox>
                    <SuiBox>
                      <ProjectStatusTag status={project.status} />
                    </SuiBox>
                  </SuiBox>
                  <SuiBox>{actions}</SuiBox>
                </SuiBox>
              </SuiBox>
            </SuiBox>
            <SuiTypography
              variant="body1"
              textTransform="none"
              color="secondary"
              lineHeight="1.2"
              sx={{
                width: "100%",
                minHeight: "32px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
              }}
            >
              {project.description
                ? project.description
                : "There is no description for this project!"}
            </SuiTypography>
            {account.account_type == "ma" ? (
              project.status == "requested" ? (
                formatProjectExpectDate(project)
              ) : (
                formatProjectDate(project)
              )
            ) : (
              <div>
                <div style={{ minHeight: 25, maxWidth: "100%" }}>
                  <SuiBox display="flex" sx={{ flexWrap: "wrap" }}>
                    {serviceRequired.map((s, index) => (
                      <SuiBadge
                        key={index}
                        variant="contained"
                        color="secondary"
                        badgeContent={s}
                        size="sm"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </SuiBox>
                </div>

                <SuiBox display="flex" sx={{ flexWrap: "wrap" }}>
                  <SuiBox key={"budget"} display="flex" py={1} pr={2} alignItems="center">
                    <SuiTypography
                      variant="body1"
                      fontWeight="bold"
                      textTransform="capitalize"
                      mr={0.5}
                    >
                      Budget:
                    </SuiTypography>
                    {budgetMin != budgetMax ? (
                      <SuiBox display="flex" alignItems="center">
                        <NumberFormat
                          value={budgetMin}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                          renderText={(value, props) => (
                            <SuiTypography
                              variant="body1"
                              fontWeight="regular"
                              color="text"
                              {...props}
                            >
                              {value}
                            </SuiTypography>
                          )}
                        />
                        ~{" "}
                        {budgetMax > 0 ? (
                          <NumberFormat
                            value={budgetMax}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"$"}
                            renderText={(value, props) => (
                              <SuiTypography
                                variant="body1"
                                fontWeight="regular"
                                color="text"
                                {...props}
                              >
                                {value}
                              </SuiTypography>
                            )}
                          />
                        ) : null}
                      </SuiBox>
                    ) : (
                      <NumberFormat
                        value={budgetMin}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"$"}
                        renderText={(value, props) => (
                          <SuiTypography
                            variant="body1"
                            fontWeight="regular"
                            color="text"
                            {...props}
                          >
                            {value}
                          </SuiTypography>
                        )}
                      />
                    )}
                  </SuiBox>
                </SuiBox>
              </div>
            )}
            <SuiBox
              display="flex"
              justifyContent="space-between"
              alignitems="center"
              sx={{ gap: 1 }}
            >
              <div>
                {room ? (
                  <SuiBox className="flex items-center">
                    <Badge variant="dot" color="error" invisible={room.ts <= room.viewts}>
                      <ContactAvatar room={room} />
                    </Badge>
                    <div className="w-full">
                      <div className="flex justify-between items-center">
                        <SuiTypography
                          variant="body1"
                          fontWeight="bold"
                          textTransform="capitalize"
                          className={`block ml-2 text-gray-600 font-semibold`}
                        >
                          {room.name}
                        </SuiTypography>
                        <SuiTypography variant="caption" className={`block ml-2 text-gray-600`}>
                          {smartdate.format(new Date(room.ts))}
                        </SuiTypography>
                      </div>
                      <div className="flex justify-between">
                        <SuiTypography
                          className={`ml-2 text-gray-600`}
                          variant="caption"
                          textTransform="none"
                          color="secondary"
                          lineHeight="1.2"
                          sx={{
                            width: "100%",
                            minHeight: "28px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "2",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {room.lastcontent}
                        </SuiTypography>
                      </div>
                    </div>
                  </SuiBox>
                ) : (
                  DesignerCom
                )}
              </div>
              <SuiBox display="flex" alignItems="center" gap={1}>
                {removeProspect ? (
                  <SuiButton
                    variant="outlined"
                    size="small"
                    color="dark"
                    sx={{ px: 2, whiteSpace: "nowrap", textTransform: "capitalize" }}
                    onClick={() => setRemoveProspectVisible(true)}
                  >
                    <span className="flex items-center justify-center">
                      <HeartBrokenIcon sx={{ height: 14, width: 14, mr: 0.5 }} />
                      <div className="xsmall:hidden small:inline">Not Interested</div>
                    </span>
                  </SuiButton>
                ) : null}
                <SuiButton
                  variant="outlined"
                  size="small"
                  color="info"
                  component={Link}
                  to={
                    account.account_type == "de"
                      ? `/produce/all-projects?id=${project.id}`
                      : `/job/requests?id=${project.id}`
                  }
                  sx={{ px: 2, textTransform: "capitalize" }}
                >
                  View
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        </SuiBox>
      </SuiBox>
      {removeProspect && removeProspectVisible ? (
        <RemoveProspectDialog
          project={project}
          userid={userid}
          option={{ fullWidth: true, maxWidth: "sm" }}
          visible={removeProspectVisible}
          okColor="error"
          okText="Remove"
          handleOk={(data) => {
            removeProspect(data);
            setRemoveProspectVisible(false);
          }}
          handleClose={() => setRemoveProspectVisible(false)}
        />
      ) : null}
    </Card>
  );
}

export default ProjectCard2;
